import React from 'react';
import styled from 'styled-components';

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Field = styled.select<{ $error?: boolean }>`
  width: 100%;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => (p.$error ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

const ErrorText = styled.span`
  font-size: 0.75rem;
  color: ${(p) => p.theme.error};
`;

export interface SelectOptionItem {
  value: string;
  label: string;
  /** Greys the option out - e.g. a value another row already took. */
  disabled?: boolean;
}

/** A block of options rendered under an `<optgroup>` heading. */
export interface SelectOptionGroup {
  label: string;
  options: SelectOptionItem[];
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  /** Ungrouped options, rendered before any group - also where a placeholder goes. */
  options?: SelectOptionItem[];
  /** Optional `<optgroup>` blocks, rendered in the given order. */
  groups?: SelectOptionGroup[];
  error?: string;
  containerStyle?: React.CSSProperties;
}

const renderOption = (opt: SelectOptionItem) => (
  <option key={opt.value} value={opt.value} disabled={opt.disabled}>
    {opt.label}
  </option>
);

export const Select: React.FC<SelectProps> = ({
  label,
  options,
  groups,
  error,
  containerStyle,
  id,
  ...rest
}) => (
  <Group style={containerStyle}>
    {label && <Label htmlFor={id}>{label}</Label>}
    <Field id={id} $error={!!error} {...rest}>
      {(options ?? []).map(renderOption)}
      {(groups ?? []).map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map(renderOption)}
        </optgroup>
      ))}
    </Field>
    {error && <ErrorText>{error}</ErrorText>}
  </Group>
);

export default Select;
